import React from 'react';
import { ExternalLink, Calendar, BookOpen, TrendingUp } from 'lucide-react';

const Articles = () => {
  const articles = [
    {
      title: "🚀 System Prompt vs User Prompt — The Blueprint vs The Request",
      platform: "LinkedIn",
      type: "Post",
      description: "Understanding the fundamental difference between system prompts and user prompts in AI interactions and their impact on AI behavior.",
      url: "https://www.linkedin.com/posts/swastik-nanda-gen-ai_system-prompt-vs-user-prompt-activity-7368633420886736896-c_mO?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA95ERUBStIgW9NztPhT7nNenHtYozkFamA",
      category: "AI/Prompt Engineering",
      featured: true
    },
    {
      title: "The Ultimate Guide to Prompt Engineering: Mastering the Art of AI Communication",
      platform: "LinkedIn",
      type: "Article",
      description: "A comprehensive guide covering advanced prompt engineering techniques, best practices, and real-world applications for effective AI communication.",
      url: "https://www.linkedin.com/pulse/ultimate-guide-prompt-engineering-mastering-art-ai-swastik-nanda-2alxf",
      category: "AI/Prompt Engineering",
      featured: true
    },
    {
      title: "🚨 The $100B LLM Secret That 99% of Developers Are Getting Wrong",
      platform: "Medium",
      type: "Article",
      description: "Revealing critical insights about LLM implementation that most developers overlook, potentially saving businesses millions in optimization costs.",
      url: "https://nandaswastik.medium.com/the-100b-llm-secret-that-99-of-developers-are-getting-wrong-081738c68049",
      category: "AI/LLM",
      featured: true
    },
    {
      title: "The Ultimate Python Data Structures Guide: How Gen AI Helped Me Create a Comprehensive Resource",
      platform: "Medium",
      type: "Article",
      description: "Exploring how Generative AI assisted in creating an exhaustive Python data structures guide, showcasing AI-human collaboration in technical documentation.",
      url: "https://nandaswastik.medium.com/the-ultimate-python-data-structures-guide-how-gen-ai-helped-me-create-a-comprehensive-resource-32693ce3e437",
      category: "Python/AI",
      featured: false
    },
    {
      title: "EWACS (Early Warning And Control System) - A Preventative self-healing tool for SAP PI environment",
      platform: "Medium",
      type: "Technical Article",
      description: "Deep dive into the automation tool I developed that reduces manual intervention by 90% in SAP PI environments, featuring architecture and implementation details.",
      url: "https://nandaswastik.medium.com/ewacs-early-warning-and-control-system-a-preventative-self-healing-tool-for-sap-pi-environment-9fcfd90d5dfa",
      category: "Automation/SAP",
      featured: false
    },
    {
      title: "AI + Human = Ananta Possibilities",
      platform: "LinkedIn",
      type: "Article",
      description: "Exploring the infinite possibilities that emerge when artificial intelligence collaborates with human creativity and problem-solving capabilities.",
      url: "https://www.linkedin.com/pulse/ai-human-ananta-possibilities-swastik-nanda-hv1vf",
      category: "AI/Philosophy",
      featured: false
    }
  ];

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'LinkedIn':
        return 'from-blue-500 to-blue-600';
      case 'Medium':
        return 'from-green-500 to-green-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getCategoryColor = (category) => {
    if (category.includes('AI')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (category.includes('Python')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (category.includes('Automation')) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const featuredArticles = articles.filter(article => article.featured);
  const regularArticles = articles.filter(article => !article.featured);

  return (
    <section id="articles" className="py-12 sm:py-16 lg:py-20 bg-slate-800/50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Articles & <span className="text-blue-400">Insights</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-6 sm:mb-8"></div>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
            Sharing knowledge and insights on AI, automation, and emerging technologies through thought leadership content
          </p>
        </div>

        {/* Featured Articles */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center mb-6 sm:mb-8">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mr-3" />
            <h3 className="text-xl sm:text-2xl font-semibold text-white">Featured Articles</h3>
          </div>
          
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8">
            {featuredArticles.map((article, index) => (
              <div
                key={index}
                className="bg-slate-700/50 border border-slate-600/50 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10 group"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(article.category)}`}>
                      {article.category}
                    </span>
                    <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${getPlatformColor(article.platform)}`}>
                      {article.platform}
                    </div>
                  </div>
                  
                  <h4 className="text-base sm:text-lg font-semibold text-white mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors duration-300">
                    {article.title}
                  </h4>
                  
                  <p className="text-gray-400 text-xs sm:text-sm mb-4 line-clamp-3">
                    {article.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 flex items-center">
                      <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      {article.type}
                    </span>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-300 text-xs sm:text-sm font-medium"
                    >
                      Read More
                      <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* All Articles */}
        <div>
          <div className="flex items-center mb-6 sm:mb-8">
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mr-3" />
            <h3 className="text-xl sm:text-2xl font-semibold text-white">All Publications</h3>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {regularArticles.map((article, index) => (
              <div
                key={index}
                className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4 sm:p-6 hover:border-blue-500/50 hover:bg-slate-700/50 transition-all duration-300 group"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(article.category)}`}>
                        {article.category}
                      </span>
                      <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${getPlatformColor(article.platform)}`}>
                        {article.platform}
                      </div>
                    </div>
                    
                    <h4 className="text-lg sm:text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                      {article.title}
                    </h4>
                    
                    <p className="text-gray-400 text-sm sm:text-base mb-3">
                      {article.description}
                    </p>
                  </div>
                  
                  <div className="lg:ml-6 flex-shrink-0">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 sm:px-6 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 text-sm sm:text-base w-full sm:w-auto justify-center"
                    >
                      Read Article
                      <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Publication Stats */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8 text-center">
            Content Impact
          </h3>
          
          <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
            <div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-400 mb-2">6+</div>
              <p className="text-gray-400 text-xs sm:text-sm">Published Articles</p>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-purple-400 mb-2">3+</div>
              <p className="text-gray-400 text-xs sm:text-sm">Platforms</p>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-400 mb-2">AI/Tech</div>
              <p className="text-gray-400 text-xs sm:text-sm">Focus Areas</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Articles;